type TagStructure = {
  [key: string]: string | TagStructure;
};

const createTags = <T extends TagStructure>(namespace: string, structure: T): T => {
  return Object.fromEntries(
    Object.entries(structure).map(([key, value]) => {
      if (typeof value === 'string') {
        return [key, `${namespace}:${value}`];
      } else {
        return [key, createTags(`${namespace}-${key.toLowerCase()}`, value)];
      }
    }),
  ) as T;
};

export const AuthTags = Object.freeze({
  APPOINTMENTS: createTags('appointments', {
    VIEWALL: 'ViewAll',
    VIEW: 'View',
    UPDATE: 'Update',
    BULKUPDATE: 'BulkUpdate',
    DOWNLOAD: 'Download',
    CANCEL: 'Cancel',
    SUPPORTCOORDINATION: { VIEW: 'View', UPDATE: 'Update' },
    CASEMANAGEMENT: { VIEW: 'View', UPDATE: 'Update', SETTINGS: 'Settings' },
    LEAVEENTRIES: { VIEW: 'View' },
    TRANSPORT: { VIEW: 'View' },
    GROUPSESSIONS: { VIEW: 'View' },
    GROUPS: { VIEW: 'View' },
    TAVGROUPS: { VIEW: 'View' },
    REPORTS: {
      REPEATVSACTUALS: { VIEW: 'View' },
    },
    EMAILSCHEDULES: { VIEW: 'View', SEND: 'Send' },
    CLIENTSCHEDULES: { VIEW: 'View', SEND: 'Send' },
    PROVIDERBILLING: { VIEW: 'View', SEND: 'Send' },
    CAREWORKERS: { VIEW: 'View' },
    TIMESHEETS: { VIEW: 'View' },
    ACTIVITIES: { VIEW: 'View' },
    PAYMENTS: { VIEW: 'View', UPDATE: 'Update' },
    EQUIPMENT: { VIEW: 'View', UPDATE: 'Update' },
    MEDICATIONS: { VIEW: 'View', UPDATE: 'Update' },
    NDIS: { VIEW: 'View' },
    SERVICEBRANCHES: { VIEW: 'View' },
  }),
  DASHBOARD: createTags('dashboard', {
    VIEW: 'View',
    UPCOMINGAPPOINTMENTS: { VIEW: 'View' },
  }),
  DASHBOARDS: createTags('dashboards', {
    CAREWORKERS: { VIEW: 'View' },
  }),
  BUDGETS: createTags('budgets', {
    VIEW: 'View',
  }),
  STATEMENTS: createTags('statements', {
    VIEW: 'View',
    HOMECARE: { VIEW: 'View' },
    HOMECAREIPA: { VIEW: 'View' },
    RESIDENTIAL: { VIEW: 'View' },
    CHSP: { VIEW: 'View' },
    HACC: { VIEW: 'View' },
    NATSIFAC: { VIEW: 'View' },
    NDIS: { VIEW: 'View' },
    NDISQUOTES: { VIEW: 'View' },
    NDISINVOICES: { VIEW: 'View' },
    BROKERAGE: { VIEW: 'View' },
    PRIVATE: { VIEW: 'View' },
    RESIDENTIALCDCBUDGET: { VIEW: 'View' },
    ITEMEXPORT: { VIEW: 'View' },
  }),
  TRANSPORT: createTags('transport', {
    VIEW: 'View',
    TRIPS: { VIEW: 'View' },
  }),
  USERS: createTags('users', {
    VIEWALL: 'ViewAll',
    ADMIN: { VIEW: 'View', UPDATE: 'Update' },
    AVAILABILITY: { VIEW: 'View' },
    SMS: { BULKSEND: 'BulkSend' },
    VOLUNTEERS: { VIEW: 'View' },
    TEAMS: { VIEW: 'View' },
  }),
  CLIENTS: createTags('clients', {
    VIEW: 'View',
    INFO: { VIEW: 'View' },
    BUDGETS: {
      VIEW: 'View',
      UPDATE: 'Update',
    },
  }),
  COMPANY: createTags('company', {
    CLIENTNOTES: { VIEW: 'View' },
    CLIENTHOLDPERIODS: { VIEW: 'View' },
    CUSTOMFORMS: { VIEW: 'View' },
    FORMS: { VIEW: 'View' },
    INCIDENTS: { VIEW: 'View' },
    SMS: { BULKSEND: 'BulkSend' },
    CLIENTPORTAL: { VIEW: 'View' },
    CLIENTS: {
      VIEW: 'View',
    },
    API: {
      UPDATE: 'Update',
    },
  }),
  EQUIPMENT: createTags('equipments', {
    VIEW: 'View',
  }),
  LEAVEREQUESTS: createTags('leaverequests', {
    VIEW: 'View',
    UPDATE: 'Update',
  }),
  INVOICES: createTags('invoices', {
    VIEW: 'View',
    SUPPLIERS: { VIEW: 'View' },
    SUPPLIERSWORKFLOW: { VIEW: 'View' },
    NDIS: { VIEW: 'View' },
  }),
  PAYROLL: createTags('payroll', {
    VIEW: 'View',
    TCONNECT: { VIEW: 'View' },
  }),
  DOCUMENTS: createTags('documents', {
    VIEW: 'View',
  }),
  REPORTS: createTags('reports', {
    VIEW: 'View',
    DVA: { VIEW: 'View' },
    DVANURSING: { VIEW: 'View' },
    HACCVIC: { VIEW: 'View' },
    HACC: { VIEW: 'View' },
    ALLIEDHEALTH: { VIEW: 'View' },
    SOAP: { VIEW: 'View' },
    ABF: { VIEW: 'View' },
  }),
  SETTINGS: createTags('settings', {
    VIEW: 'View',
    EQUIPMENT: { VIEW: 'View' },
    ADMIN: { VIEW: 'View', UPDATE: 'Update' },
    DEX: { VIEW: 'View', UPDATE: 'Update' },
    HACC: { VIEW: 'View' },
    BANKDETAILS: { VIEW: 'View', UPDATE: 'Update' },
    DVANURSING: { VIEW: 'View', UPDATE: 'Update' },
    HRMATRIX: { VIEW: 'View', UPDATE: 'Update' },
    BILLINGMATRIX: { VIEW: 'View', UPDATE: 'Update' },
    PACKAGERESTRICTIONS: { VIEW: 'View', UPDATE: 'Update' },
    ACCOUNTLOCK: { VIEW: 'View', UPDATE: 'Update' },
  }),
  PROSPECTS: createTags('prospects', {
    VIEW: 'View',
  }),
  SUPPORTATHOME: createTags('supportathome', {
    VIEW: 'View',
    DASHBOARD: { VIEW: 'View' },
    BUDGETPLANNER: { VIEW: 'View' },
    SUPPLIERINVOICES: {
      VIEW: 'View',
      UPDATE: 'Update',
      APPROVE: 'Approve',
      SENIOR: 'SeniorApprove',
      ADMIN: 'Admin',
      OVERRIDE: 'Override',
      SETTINGS: 'Settings',
    },
    CLAIMS: { VIEW: 'View', UPDATE: 'Update', ADMIN: 'Admin', UNLOCK: 'Unlock' },
  }),
  CLIENTS_PACKAGES: createTags('clients-packages', {
    VIEW: 'View',
    UPDATE: 'Update',
  }),
  DIRECTDEBITS: createTags('directdebits', {
    VIEW: 'View',
    UPDATE: 'Update',
    UNLOCK: 'Unlock',
  }),
  WEEKLYPLANNER: createTags('weeklyplanner', {
    VIEW: 'View',
  }),
  // Custom Forms (TAV-2658+). Resolves to `forms:View` / `forms:Edit` to match the API access tags
  // seeded in lookup_access_tags (module 'forms', no name). Distinct from the legacy V1 forms page,
  // which uses COMPANY.FORMS (`company-forms:View`).
  FORMS: createTags('forms', {
    VIEW: 'View',
    EDIT: 'Edit',
  }),
});
